import { activitySourceKeys } from './activityPresentation'
import type { AssistantActivitySubject, AssistantActivitySourceKind, AssistantActivitySourceResult } from '@digital-twins/shared-types/assistant-activity'
import type { AssistantToolName, AssistantStageName } from '@digital-twins/shared-types/assistant'



import { Text } from '@/components/ui/Text'
import {
  ActivityTargetKey,
  getThreadPlaceholders,
  resolveThreadLanguage,
  ThreadPlaceholderTemplates,
} from '@/lib/constants/languages'
import { TwinsMarkLoader } from '@/components/quick-study-results/TwinsMarkLoader'

import { ActivityPageData } from './resolveActivityObject'
import { activityLiveText, ThreadActivitySteps } from './ThreadActivitySteps'

/**
 * What the thread shows between sending a question and the first token.
 *
 * Retrieval runs before generation, so this gap is routinely several seconds on
 * a large library, long enough that an empty bubble reads as a hang.
 *
 * WHAT IT SAYS IS DRIVEN BY THE STREAM, not by a clock. The turn already
 * announces every leg it runs (`tool_start`) and every leg that finishes
 * (`tool_end`), and `ThreadDetailPage` collects those into the `legs` prop.
 * `tool_start.query` is the model's own lookup string when it sent one.
 *
 * Phase copy lives in `THREAD_PLACEHOLDERS`. A new tool needs a target and a
 * chip noun in every language there. Chip glyphs are a separate listing:
 * `ACTIVITY_CHIP_ICONS`. A tool with copy but no glyph still renders, because
 * the fallback icon is honest about "a source" rather than crashing. A tool
 * with no fragment yet still renders the generic working line.
 *
 * Before any step reports, the row is the mark plus the `working` line. From
 * the first `tool_start` on, it is the `ThreadActivitySteps` checklist: one
 * row per step, finished rows kept with their outcome, and a trailing
 * `composing` row once every step has reported
 * (docs/plans/thread-activity-steps.md). The older chip helpers below
 * (`threadActivityLabel`, `threadActivityChips`) stay as the pure phase rules;
 * the label still words the no-steps line.
 *
 * Only one visually hidden span is a live region, kept mounted for the whole
 * wait, so a growing list does not re-read itself on every frame and the
 * first step is not lost to a region that mounts already full (F11).
 *
 * The mark is the platform's own waiting glyph (TwinsMarkLoader), the same one
 * Quick Study shows while it thinks: it already carries the motion, so nothing
 * else in this row needs to animate.
 *
 * The copy follows the THREAD's language, not the browser's. This line sits in
 * the same column as the answer that replaces it, so an English wait ahead of a
 * Portuguese answer was the seam worth closing
 * (docs/plans/home-lab-composer-language-and-recent-study.md, D3 and D4).
 */

/**
 * One retrieval leg as the waiting line sees it: what it is, and whether it has
 * reported back.
 *
 * `tool` is the wire's `AssistantToolName` but is typed as a plain string on
 * purpose. This component must render a leg whose name it has never heard of
 * rather than fail to compile against a frozen union, which is the whole point
 * of deriving the phase from counts instead of from names.
 */
export interface ThreadActivityLeg {
  tool: string
  /** Flipped by that leg's `tool_end`, whether it succeeded or failed. */
  done: boolean
  /** Model lookup string from `tool_start.query`, when the call carried one. */
  query?: string
  /**
   * The provider call id from `tool_start.callId`, when the producer sends it.
   * Pairs this leg with its own `tool_end` even when two legs of the same tool
   * run at once (`applyActivityFrame`). Absent on older producers.
   */
  callId?: string
  /**
   * D8: what a `document_search` leg searched, from `tool_start.source_kinds`.
   * Absent on older producers, which reads as the library alone.
   */
  sourceKinds?: Array<AssistantActivitySourceKind>
  subject?: AssistantActivitySubject
  sourceResults?: Array<AssistantActivitySourceResult>
  /** `tool_end.ok`. Absent while running, and on a leg the stream never closed. */
  ok?: boolean
  /** `tool_end.itemCount`. Only search steps print it (D4). */
  itemCount?: number
}

export interface ThreadActivityChip {
  tool: string
  label: string
  query: string | null
}

interface ThreadActivityIndicatorProps {
  /** The thread's answer language. Null or absent falls back to English. */
  language?: string | null
  /**
   * Legs reported so far, in `tool_start` order. Absent or empty is a real
   * state, not missing data: it is every turn before its first frame lands, and
   * every turn that plans no retrieval at all.
   */
  legs?: Array<ThreadActivityLeg>
  /** What the page holds to name a step's object (D5). */
  pageData?: ActivityPageData | null
}

/** Long enough to name the topic; short enough to stay on one quiet line. */
const ACTIVITY_QUERY_MAX = 56

/**
 * Joins target fragments the way the thread's own language joins a list.
 *
 * `Intl.ListFormat` rather than a per-language conjunction in the copy table:
 * the conjunction, the separators and whether a list takes one at all are facts
 * about the language, and the platform ships one already. The guard is for a
 * runtime without it, where a comma-joined list is worse prose but still true.
 */
function formatTargets(targets: Array<string>, language: string): string {
  if (targets.length < 2) {
    return targets[0] ?? ''
  }

  try {
    return new Intl.ListFormat(language, {
      style: 'long',
      type: 'conjunction',
    }).format(targets)
  } catch {
    return targets.join(', ')
  }
}

export function clipActivityQuery(query: string | undefined): string | null {
  if (!query) {
    return null
  }

  const collapsed = query.trim().replace(/\s+/g, ' ')
  if (!collapsed) {
    return null
  }

  if (collapsed.length <= ACTIVITY_QUERY_MAX) {
    return collapsed
  }

  return `${collapsed.slice(0, ACTIVITY_QUERY_MAX - 1)}\u2026`
}

/**
 * Running named legs, one per tool. A turn can announce `web_search` twice;
 * the sentence and the chips both name the source once.
 */
function uniqueNamedRunning(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
}): Array<{ key: ActivityTargetKey; leg: ThreadActivityLeg }> {
  const { legs, copy } = params
  const seen = new Set<string>()
  const unique: Array<{ key: ActivityTargetKey; leg: ThreadActivityLeg }> = []

  for (const leg of legs) {
    if (leg.done) {
      continue
    }

    for (const key of activityTargetKeys(leg)) {
      if (!activityValue(copy.targets, key) || seen.has(key)) {
        continue
      }

      seen.add(key)
      unique.push({ key, leg })
    }
  }

  return unique
}

/**
 * The target keys one running leg names. A `document_search` leg that carries
 * `sourceKinds` expands into the library and/or the trackers (D8); every other
 * leg, and a `document_search` without kinds, names itself.
 */
function activityTargetKeys(leg: ThreadActivityLeg): Array<ActivityTargetKey> {
  return activitySourceKeys(leg)
}

/**
 * The line to show for a set of legs. Pure, and exported so the phase rules can
 * be tested without a render.
 *
 * Three phases, in the order a turn passes through them:
 *
 *   no legs yet          -> `working`, which claims nothing it cannot back
 *   at least one open    -> `searching` / `searchingFor`, naming only the
 *                           legs STILL RUNNING (and one query when present)
 *   every leg reported   -> `composing`, because retrieval is genuinely over
 *
 * A leg that ended is dropped from the sentence whether it succeeded or failed.
 * The waiting line's job is to say what the reader is waiting ON, and a finished
 * leg is not it. What a failed leg produced belongs in the answer, which already
 * reports its own gaps.
 */
export function threadActivityLabel(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
  /** Already resolved through `resolveThreadLanguage`. */
  language: string
}): string {
  const { legs, copy, language } = params

  if (legs.length === 0) {
    return copy.working
  }

  const running = legs.filter((leg) => !leg.done)
  if (running.length === 0) {
    return copy.composing
  }

  const named = uniqueNamedRunning({ legs, copy })
  const targets = named
    .map(({ key }) => activityValue(copy.targets, key))
    .filter((target): target is string => Boolean(target))

  if (targets.length === 0) {
    return copy.working
  }

  const query = clipActivityQuery(
    named.find(({ leg }) => Boolean(leg.query?.trim()))?.leg.query,
  )
  const filled = (query ? copy.searchingFor : copy.searching).replace(
    '{targets}',
    formatTargets(targets, language),
  )

  return query ? filled.replace('{query}', query) : filled
}

/**
 * Citation-style chips for the visible row. Empty when the turn is not in the
 * searching phase (no named running tool), so the component can fall back to
 * the plain sentence.
 */
export function threadActivityChips(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
}): Array<ThreadActivityChip> {
  const { legs, copy } = params

  if (legs.length === 0 || legs.every((leg) => leg.done)) {
    return []
  }

  return uniqueNamedRunning({ legs, copy })
    .map(({ key, leg }): ThreadActivityChip | null => {
      const label = activityValue(copy.chips, key)
      if (!label) {
        return null
      }

      return {
        tool: key,
        label,
        query: clipActivityQuery(leg.query),
      }
    })
    .filter((chip): chip is ThreadActivityChip => chip != null)
}

/**
 * Glyph per named chip. `read_audience` reuses the app's audience entity icon,
 * so the chip matches the sidebar.
 */
export { ACTIVITY_CHIP_ICONS } from './activityIcons'


function activityValue<T>(values: Record<ActivityTargetKey, T>, tool: string): T | undefined {
  return Object.hasOwn(values, tool) ? values[tool as keyof typeof values] : undefined
}

export function ThreadActivityIndicator(props: ThreadActivityIndicatorProps) {
  const { language, legs, pageData } = props

  const resolved = resolveThreadLanguage(language)
  const copy = getThreadPlaceholders(resolved)
  const resolvedLegs = legs ?? []
  const label = threadActivityLabel({
    legs: resolvedLegs,
    copy,
    language: resolved,
  })

  const hasLegs = resolvedLegs.length > 0
  /* One hidden live region for the whole wait, mounted in both branches at
     the same position, so the first `tool_start` updates it in place rather
     than mounting a fresh region already holding text that a screen reader
     would never announce (F11). */
  const liveText = hasLegs
    ? activityLiveText({ legs: resolvedLegs, copy, language: resolved, pageData })
    : label

  /* Once a step has reported in, the checklist takes over: one row per step,
     finished rows stay visible (docs/plans/thread-activity-steps.md). */
  const visible = hasLegs ? (
    <ThreadActivitySteps
      legs={resolvedLegs}
      language={language}
      pageData={pageData}
      isLive
    />
  ) : (
    <>
      <TwinsMarkLoader
        className="h-4 w-4 text-muted-foreground"
        dataTestId="thread-activity-mark"
      />
      <Text size="sm" color="muted" aria-hidden>
        {label}
      </Text>
    </>
  )

  return (
    <div
      className={hasLegs ? undefined : 'flex items-center gap-2.5'}
      data-testid="thread-activity-indicator"
    >
      {visible}
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        data-testid="thread-activity-live"
      >
        {liveText}
      </span>
    </div>
  )
}
